import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
  Typography,
} from '@mui/material';
import { useCreateUsuario, useUpdateUsuario, useEmpresasActivas } from '../../hooks/useUsuarios';
import type { Usuario, UserRole } from '../../types/usuario';
import { getRolLabel } from '../../types/usuario';
import { zCuit } from '../../utils/validations';
import { formatCuit } from '../../utils/format';

// ── Schemas ───────────────────────────────────────────────────────────────────

const rolesDisponibles: UserRole[] = ['admin_general', 'operador', 'admin_empresa'];

// Schemas con helpers centralizados (#031) + reglas extra de la pantalla.
const createSchema = z
  .object({
    // CUIT: identificador de login (11 dígitos, único, validado con AFIP).
    cuit: zCuit('CUIT'),
    // Email normalizado (trim + lowercase) — ahora es el correo de CONTACTO.
    email: z
      .string({ required_error: 'Email es requerido' })
      .trim()
      .min(1, 'Email es requerido')
      .email('Email no tiene un formato válido')
      .max(120, 'Email no puede superar 120 caracteres')
      .transform((v) => v.toLowerCase()),
    // Password: mantenemos política más estricta acá (mayúscula + número)
    // que zPasswordFuerte (que solo exige número) — los usuarios admin tienen
    // que cumplir el estándar fuerte. Decisión #022.
    password: z
      .string({ required_error: 'Contraseña es requerida' })
      .min(8, 'Contraseña debe tener al menos 8 caracteres')
      .max(72, 'Contraseña no puede superar 72 caracteres') // bcrypt limit
      .regex(/[A-Z]/, 'Contraseña debe contener al menos una mayúscula')
      .regex(/[0-9]/, 'Contraseña debe contener al menos un número'),
    nombre: z
      .string({ required_error: 'El nombre es requerido' })
      .trim()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .max(100, 'El nombre no puede superar 100 caracteres'),
    apellido: z
      .string()
      .trim()
      .max(100, 'El apellido no puede superar 100 caracteres')
      .optional(),
    rol: z.enum(['admin_general', 'operador', 'admin_empresa']),
    empresa_id: z.string().uuid('Seleccioná una empresa').optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.rol === 'admin_empresa' && !data.empresa_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Seleccioná la empresa para este usuario',
        path: ['empresa_id'],
      });
    }
  });

const editSchema = z
  .object({
    nombre: z
      .string({ required_error: 'El nombre es requerido' })
      .trim()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .max(100, 'El nombre no puede superar 100 caracteres'),
    apellido: z
      .string()
      .trim()
      .max(100, 'El apellido no puede superar 100 caracteres')
      .optional(),
    rol: z.enum(['admin_general', 'operador', 'admin_empresa']),
    empresa_id: z.string().uuid('Seleccioná una empresa').optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.rol === 'admin_empresa' && !data.empresa_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Seleccioná la empresa para este usuario',
        path: ['empresa_id'],
      });
    }
  });

type CreateValues = z.infer<typeof createSchema>;
type EditValues   = z.infer<typeof editSchema>;

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  usuario: Usuario | null; // null = alta
  onClose: () => void;
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function UsuarioDialog({ open, usuario, onClose }: Props) {
  const isEdit = !!usuario;
  const create = useCreateUsuario();
  const update = useUpdateUsuario();
  const { data: empresas = [] } = useEmpresasActivas();

  // El schema y los tipos cambian según create/edit
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(isEdit ? editSchema : createSchema) as never,
  });

  const rolWatched = watch('rol');

  useEffect(() => {
    if (!open) return;
    if (usuario) {
      reset({
        nombre:     usuario.nombre,
        apellido:   usuario.apellido ?? '',
        rol:        usuario.rol,
        empresa_id: usuario.empresa_id ?? null,
      } as CreateValues);
    } else {
      reset({
        cuit:       '',
        email:      '',
        password:   '',
        nombre:     '',
        apellido:   '',
        rol:        'operador',
        empresa_id: null,
      } as CreateValues);
    }
  }, [open, usuario, reset]);

  const onSubmit = async (values: CreateValues) => {
    try {
      if (isEdit && usuario) {
        const payload: EditValues = {
          nombre:     values.nombre,
          apellido:   values.apellido || undefined,
          rol:        values.rol,
          empresa_id: values.rol === 'admin_empresa' ? (values.empresa_id ?? null) : null,
        };
        await update.mutateAsync({ id: usuario.id, payload });
      } else {
        await create.mutateAsync({
          cuit:       values.cuit!,
          email:      values.email!,
          password:   values.password!,
          nombre:     values.nombre,
          apellido:   values.apellido || null,
          rol:        values.rol,
          empresa_id: values.rol === 'admin_empresa' ? (values.empresa_id ?? null) : null,
        });
      }
      onClose();
    } catch {
      // el error se muestra abajo
    }
  };

  const apiError = (isEdit ? update.error : create.error) as Error | null;

  // Mensaje de error amigable
  const errorMsg = (() => {
    if (!apiError) return null;
    const msg = apiError.message;
    if (
      msg.toLowerCase().includes('already registered') ||
      msg.toLowerCase().includes('ese cuit') ||
      msg.includes('409')
    ) {
      return 'Ya existe un usuario con ese CUIT.';
    }
    return msg;
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>
          {isEdit
            ? `Editar usuario — ${usuario?.nombre} ${usuario?.apellido ?? ''}`
            : 'Nuevo usuario'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

            {/* Datos de acceso — solo en alta */}
            {!isEdit && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                  CREDENCIALES DE ACCESO
                </Typography>
                <TextField
                  {...register('cuit')}
                  required
                  fullWidth
                  label="CUIT"
                  placeholder="20-12345678-3"
                  autoComplete="off"
                  error={!!errors.cuit}
                  helperText={errors.cuit?.message ?? 'Se usa para iniciar sesión'}
                />
                <TextField
                  {...register('email')}
                  required
                  fullWidth
                  label="Correo electrónico"
                  type="email"
                  autoComplete="off"
                  error={!!errors.email}
                  helperText={errors.email?.message ?? 'Correo de contacto (para recuperar la clave)'}
                />
                <TextField
                  {...register('password')}
                  required
                  fullWidth
                  label="Contraseña inicial"
                  type="password"
                  autoComplete="new-password"
                  error={!!errors.password}
                  helperText={
                    errors.password?.message ??
                    'Mínimo 8 caracteres, 1 mayúscula y 1 número. El usuario puede cambiarla después.'
                  }
                />
                <Divider />
              </>
            )}

            {/* CUIT (login) y correo de contacto — solo lectura en edición */}
            {isEdit && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  value={usuario?.cuit ? formatCuit(usuario.cuit) : '—'}
                  fullWidth
                  label="CUIT"
                  disabled
                  helperText="Identificador de login (no se puede modificar)"
                />
                <TextField
                  value={usuario?.email ?? ''}
                  fullWidth
                  label="Correo electrónico"
                  disabled
                  helperText="Correo de contacto"
                />
              </Stack>
            )}

            {/* Datos personales */}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              DATOS PERSONALES
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                {...register('nombre')}
                required
                fullWidth
                label="Nombre"
                error={!!errors.nombre}
                helperText={errors.nombre?.message}
              />
              <TextField
                {...register('apellido')}
                fullWidth
                label="Apellido"
                error={!!errors.apellido}
                helperText={errors.apellido?.message}
              />
            </Stack>

            {/* Rol y empresa */}
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              ROL Y PERMISOS
            </Typography>

            <Controller
              name="rol"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  required
                  label="Rol"
                  error={!!errors.rol}
                  helperText={errors.rol?.message}
                >
                  {rolesDisponibles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {getRolLabel(r)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Selector de empresa — solo si rol = admin_empresa */}
            {rolWatched === 'admin_empresa' && (
              <Controller
                name="empresa_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    select
                    fullWidth
                    required
                    label="Empresa"
                    error={!!errors.empresa_id}
                    helperText={
                      errors.empresa_id?.message ??
                      'Empresa a la que pertenece este usuario'
                    }
                  >
                    {empresas.length === 0 && (
                      <MenuItem disabled value="">
                        No hay empresas activas
                      </MenuItem>
                    )}
                    {empresas.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.razon_social}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting
              ? 'Guardando…'
              : isEdit
              ? 'Guardar cambios'
              : 'Crear usuario'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
